import React, { useEffect, Suspense, lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AlertTriangle } from "lucide-react";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { PWAUpdater } from "@/components/PWAUpdater";
import { PointsAwarded } from "@/components/gamification/PointsAwarded";
import ProfileCompletionGuard from "@/components/auth/ProfileCompletionGuard";
import { supabase } from "@/integrations/supabase/client";
import { useScreenshotProtection } from './hooks/useScreenshotProtection';
import { LogoLoader } from "@/components/ui/LogoLoader";
import { GlobalLoadingProvider, useGlobalLoading, GlobalLoaderTrigger } from "@/contexts/GlobalLoadingContext";
import { GlobalLoadingScreen } from "@/components/ui/GlobalLoadingScreen";
import { AdminLiveActivityFeed } from "@/components/admin/AdminLiveActivityFeed";

import Login from "./pages/Login";
import Home from "./pages/Home";
import SharedAlbum from "./pages/SharedAlbum";

const Videos = lazy(() => import("./pages/Videos"));
const Albums = lazy(() => import("./pages/Albums"));
const Profile = lazy(() => import('./pages/Profile'));
const TotemScreen = lazy(() => import('./pages/TotemScreen').then(m => ({ default: m.TotemScreen })));
const MyPurchases = lazy(() => import("./pages/MyPurchases"));
const MyPlays = lazy(() => import("./pages/MyPlays"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Subscription = lazy(() => import("./pages/Subscription"));
const Ranking = lazy(() => import("./pages/Ranking"));
const Admin = lazy(() => import("./pages/Admin"));
const Store = lazy(() => import("./pages/Store"));
const Designer = lazy(() => import("./pages/Designer"));
const MotionEditor = lazy(() => import("./pages/MotionEditor"));
const HudyMotionLanding = lazy(() => import("./pages/HudyMotionLanding"));
const HudyHotsite = lazy(() => import("./pages/HudyHotsite"));
const ContractView = lazy(() => import("./pages/ContractView"));
const Futlink = lazy(() => import("./pages/Futlink"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const GuestSuccess = lazy(() => import("./pages/GuestSuccess"));
const StreamingClient = lazy(() => import("./pages/StreamingClient"));
const StreamingReceiver = lazy(() => import("./pages/StreamingReceiver"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ScoreboardOverlay = lazy(() => import("./pages/ScoreboardOverlay"));
const CountdownOverlay = lazy(() => import("./pages/CountdownOverlay"));
const GamesPortal = lazy(() => import("./pages/GamesPortal"));
const GameDetails = lazy(() => import("./pages/GameDetails"));
const GameSessions = lazy(() => import("./pages/GameSessions"));
const GameSessionDetail = lazy(() => import("./pages/GameSessionDetail"));
const GameListPublic = lazy(() => import("./pages/GameListPublic"));
const EbookArbitro = lazy(() => import("./pages/EbookArbitro"));

const VisitTracker = () => {
 const location = useLocation();
 const { session } = useAuth();
 
 React.useEffect(() => {
   const checkCampaignClick = async () => {
     const params = new URLSearchParams(window.location.search);
     const campId = params.get('click_camp');
     if (campId) {
       try {
         await supabase.rpc('increment_campaign_click', { cid: campId });
       } catch (e) {
         console.warn("Failed to track campaign click");
       }
       
       params.delete('click_camp');
       const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '') + window.location.hash;
       window.history.replaceState({}, '', newUrl);
     }
   };

   const logVisit = async () => {
     try {
       let sessionId = sessionStorage.getItem("site_session_id");
       if (!sessionId) {
         sessionId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : ('sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9));
         sessionStorage.setItem("site_session_id", sessionId);
       }

       const visitData = {
         user_id: session?.user?.id || null,
         session_id: sessionId,
         page_path: location.pathname,
         user_agent: navigator.userAgent,
       };

       await supabase.from("site_visits").insert(visitData);
     } catch (err) {
       console.warn("Failed to log visit:", err);
     }
   };

   checkCampaignClick();
   logVisit();
 }, [location.pathname, session?.user?.id]);

 return null;
};

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error?: any, errorInfo?: any }> {
 constructor(props: any) {
 super(props);
 this.state = { hasError: false, error: null, errorInfo: null };
 }

 static getDerivedStateFromError(error: any) {
 return { hasError: true, error };
 }

 componentDidCatch(error: any, errorInfo: any) {
 console.error("[FATAL ERROR] App crashed:", error, errorInfo); this.setState({ errorInfo });
 }

 render() {
 if (this.state.hasError) {
 return (
 <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
 <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-6">
 <AlertTriangle className="w-8 h-8 text-red-500" />
 </div>
 <h1 className="text-2xl font-bold text-foreground mb-2">Ops! Algo deu errado.</h1>
 <p className="text-foreground/40 text-sm mb-4 max-w-xs">Ocorreu um erro inesperado ao carregar o aplicativo.</p>
   <div className="bg-black/50 border border-red-500/30 p-4 rounded-xl text-left overflow-auto max-h-[300px] max-w-[90vw] text-xs text-red-400 mb-8 font-mono">
     <p className="font-bold text-red-500 mb-2">{this.state.error && this.state.error.toString()}</p>
     <pre className="whitespace-pre-wrap">{this.state.errorInfo && this.state.errorInfo.componentStack}</pre>
   </div>
 <button 
 onClick={() => window.location.reload()}
 className="bg-primary text-primary-foreground px-8 py-3 rounded-xl font-bold uppercase tracking-widest text-xs"
 >
 Recarregar Aplicativo
 </button>
 </div>
 );
 }

 return this.props.children;
 }
}

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
 const { session, loading } = useAuth();
  if (loading) return <GlobalLoaderTrigger />;
 if (!session) return <Navigate to="/" replace />;
 return (
 <ProfileCompletionGuard>
 {children}
 </ProfileCompletionGuard>
 );
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
 const { session, loading } = useAuth();
  if (loading) return <GlobalLoaderTrigger />;
 if (session) return <Navigate to="/home" replace />;
 return <>{children}</>;
};

const LoadingFallback = () => <GlobalLoaderTrigger />;

const AppRoutes = () => (
  <Suspense fallback={<LoadingFallback />}>
    <Routes>
      <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/futlink" element={<Futlink />} />
      <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/videos" element={<ProtectedRoute><Videos /></ProtectedRoute>} />
      <Route path="/albums" element={<ProtectedRoute><Albums /></ProtectedRoute>} />
      <Route path="/albums/:id" element={<ProtectedRoute><Albums /></ProtectedRoute>} />
      <Route path="/jogos" element={<ProtectedRoute><GamesPortal /></ProtectedRoute>} />
      <Route path="/jogos/:id" element={<ProtectedRoute><GameDetails /></ProtectedRoute>} />
      <Route path="/sessoes" element={<ProtectedRoute><GameSessions /></ProtectedRoute>} />
      <Route path="/sessoes/:slug" element={<ProtectedRoute><GameSessionDetail /></ProtectedRoute>} />
      <Route path="/convocacao/:slug" element={<GameListPublic />} />
      <Route path="/public/album/:id" element={<SharedAlbum />} />
      <Route path="/s/:id" element={<SharedAlbum />} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/totem-camera" element={<ProtectedRoute><TotemScreen /></ProtectedRoute>} />
      <Route path="/ranking" element={<ProtectedRoute><Ranking /></ProtectedRoute>} />
      <Route path="/my-records" element={<Navigate to="/profile?tab=purchases" replace />} />
      <Route path="/my-purchases" element={<Navigate to="/profile?tab=purchases" replace />} />
      <Route path="/my-plays" element={<ProtectedRoute><MyPlays /></ProtectedRoute>} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
      <Route path="/store" element={<ProtectedRoute><Store /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/designer" element={<ProtectedRoute><Designer /></ProtectedRoute>} />
      <Route path="/motion" element={<ProtectedRoute><HudyMotionLanding /></ProtectedRoute>} />
      <Route path="/motion/editor" element={<ProtectedRoute><MotionEditor /></ProtectedRoute>} />
      <Route path="/hudymotion" element={<Navigate to="/motion" replace />} />
      <Route path="/h/:id" element={<HudyHotsite />} />
      <Route path="/contract/:id" element={<ContractView />} />
      <Route path="/stream/:id" element={<StreamingClient />} />
      <Route path="/stream/view/:id" element={<StreamingReceiver />} />
      <Route path="/guest-success/:id" element={<GuestSuccess />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/overlay/placar" element={<ScoreboardOverlay />} />
      <Route path="/overlay/espera" element={<CountdownOverlay />} />
      <Route path="/ebook-arbitro" element={<EbookArbitro />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);


const ScreenshotProtection = () => {
  const isBlurred = useScreenshotProtection();

  if (!isBlurred) return null;

  return (
    <div className="fixed inset-0 z-[99999] bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center pointer-events-none transition-all duration-300 px-4">
      <div className="flex flex-col items-center animate-in fade-in zoom-in duration-500 ease-out">
        <div className="relative mb-6 drop-shadow-[0_0_30px_rgba(255,106,0,0.3)]">
          <div className="text-8xl md:text-9xl animate-bounce">📸🙅‍♂️</div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent mix-blend-overlay"></div>
        </div>
        
        <div className="bg-[#121212] border border-white/10 p-8 md:p-10 rounded-[2.5rem] max-w-md w-full text-center shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-[#ff6a00] to-transparent opacity-50"></div>
          
          <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tighter mb-4 text-transparent bg-clip-text bg-gradient-to-r from-white to-white/70">
            Opa, te peguei!
          </h2>
          <p className="text-white/50 font-medium leading-relaxed text-sm md:text-base">
            As imagens estão super protegidas por direitos autorais, então <strong className="text-white/80">não dá pra tirar print</strong>. 
            <br/><br/>
            Mas fique à vontade para garantir suas fotos em alta resolução direto na galeria! 💛
          </p>
        </div>
      </div>
    </div>
  );
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    // Timeout pequeno para garantir que o render terminou antes de rolar
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 10);
  }, [pathname, hash]);

  return null;
};

import { GlobalFloatingButtons } from "@/components/GlobalFloatingButtons";

const AppContent = () => {
  const location = useLocation();
  const isOverlay = location.pathname.startsWith('/overlay') || location.pathname.startsWith('/stream/view');
  
  useEffect(() => {
    if (isOverlay) {
      document.body.style.backgroundColor = 'transparent';
      document.documentElement.style.backgroundColor = 'transparent';
    } else {
      document.body.style.backgroundColor = '#0d0d0d';
      document.documentElement.style.backgroundColor = '';
    }
    
    // Cleanup if component unmounts
    return () => {
      document.body.style.backgroundColor = '#0d0d0d';
    };
  }, [isOverlay]);
  
  return (
    <>
      {!isOverlay && (
        <>
          <PWAUpdater />
          <PWAInstallPrompt />
          <GlobalFloatingButtons />
          <ScreenshotProtection />
          <PointsAwarded />
          <VisitTracker />
          <AdminLiveActivityFeed />
        </>
      )}
      <ScrollToTop />
      <AppRoutes />
    </>
  );
};

const App = () => (
 <ErrorBoundary>
 <QueryClientProvider client={queryClient}>
 <TooltipProvider>
 <GlobalLoadingProvider>
 <GlobalLoadingScreen />
 <Toaster />
 <Sonner />
 <AuthProvider>
 <BrowserRouter>
  <AppContent />
 </BrowserRouter>
 </AuthProvider>
 </GlobalLoadingProvider>
 </TooltipProvider>
 </QueryClientProvider>
 </ErrorBoundary>
);

export default App;


